import { TechnologySelectDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Score } from '@components/shared/score.component';
import { Tile } from '@components/shared/tile.component';
import { Button } from '@components/ui/button';
import { formatTimelineDate } from '@utils/assessment.util';
import { EditIcon } from 'lucide-react';
import { FC, useState } from 'react';
import { AccordionTile } from '../../shared/accordion-tile.component';
import { Steps, TechnologyTechnologyModal } from '../../technology-technology-modal.component';

type Trl1ContentProps = {
  canEdit: boolean;
  technology: TechnologySelectDto;
};

const Trl1Actions: FC<{ canEdit: boolean; dateLabel: string | null; onEdit: () => void }> = (props) => {
  const { canEdit, dateLabel, onEdit } = props;

  if (!canEdit) return null;

  // Show text link button when no date (always visible), IconButton when date exists (hover only)
  if (!dateLabel) {
    return (
      <Button
        onClick={onEdit}
        variant="link"
      >
        Set your TRL Date
      </Button>
    );
  }

  return (
    <IconButton.HoverReveal
      ariaLabel="Edit details"
      icon={<EditIcon size={16} />}
      onClick={onEdit}
      variant="secondary"
    />
  );
};

export const Trl1Content: FC<Trl1ContentProps> = (props) => {
  const { canEdit, technology } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const timeline = technology.timeline;

  const dateLabel = timeline ? formatTimelineDate(timeline) : null;

  const trlDescription =
    'Technology Readiness Level (TRL) is a measurement system used to assess the maturity of a particular technology. The scale ranges from 1 to 9, where TRL 1 represents basic principles observed and TRL 9 represents actual system proven through successful mission operations.';

  return (
    <div className="flex flex-col gap-4">
      {/* Technology Readiness Description */}
      <AccordionTile
        content={trlDescription}
        title="Technology Readiness Level"
        value="trl-description"
      />

      <Tile hoverReveal>
        <div className="flex flex-col gap-4">
          {/* Score + Actions */}
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-2">
              <Score
                score={1}
                size="large"
                subtitle={dateLabel ?? 'Date Not Set'}
                title="TRL 1"
                className="bg-blue-100"
                type="TRL"
              />
            </div>

            <Trl1Actions
              canEdit={canEdit}
              dateLabel={dateLabel}
              onEdit={() => setIsModalOpen(true)}
            />
          </div>

          {/* TRL 1 Description */}
          <div className="rounded-lg bg-gray-100 p-3">
            <div className="mb-1 text-sm font-medium text-gray-600">TRL 1 - Basic Principles Identified</div>
            <div className="mb-3 text-xs leading-[18px] text-gray-500">
              This score is automatically assigned when development begins. Foundational concepts are established
              through early documentation or academic work that identifies core scientific or architectural principles.
            </div>
            <div className="flex flex-col gap-2">
              <div className="text-xs font-semibold text-gray-600">At this stage:</div>
              <ul className="list-disc list-inside text-xs leading-[18px] text-gray-500 space-y-1">
                <li>Initial research and theoretical frameworks are documented</li>
                <li>Core principles are identified but not yet validated</li>
                <li>No prototyping or experimental work has begun</li>
              </ul>
            </div>
          </div>
        </div>
      </Tile>

      {canEdit && (
        <TechnologyTechnologyModal
          initialStep={Steps.RESEARCH}
          isEdit={true}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          technology={technology}
        />
      )}
    </div>
  );
};
